import React, { useState, useEffect } from "react";
import { Button } from "semantic-ui-react";
import moment from "moment";
import { formatMoneyWithCurrency, formatRut } from "src/utils/formatter";
import { banksFormat } from "src/utils/bankList";
import FilterTabs from "src/components/FilterTabs";
import CustomModal from "src/components/configuracion/components/customModal";
import "src/components/documentostributarios/documentTypes/TaxDocumentDetail.css";
import { FindTransactionById } from "src/graphql/conciliacion";
import LoadingSpinner from "src/components/general/js/spinnerLoading";

/**
 * ModalTransactionDetail - Modal to show bank movement details.
 * Uses the exact same design pattern as DocumentDetailContainer + TaxDocumentDetail:
 * - CustomModal wrapper (red header with white text, rounded corners)
 * - TaxDocumentDetail.css classes for body layout (document-stamp, info-block, totals-card, etc.)
 */
const ModalTransactionDetail = ({ isVisible, onClose, transaction }) => {
  const [activeTab, setActiveTab] = useState("detalles");

  const transactionId = transaction?.id || transaction?._id;

  const { data, fetching, reexecuteQuery } = FindTransactionById({
    ids: transactionId && isVisible ? [transactionId] : [],
  });

  // Force a fresh network fetch every time the modal opens
  useEffect(() => {
    if (isVisible && transactionId) {
      reexecuteQuery({ requestPolicy: "network-only" });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, transactionId]);

  if (!transaction) return null;

  // Use fresh server data if available, fall back to prop
  const tx = data?.findTransactionsById?.[0] || transaction;

  const bankId = tx.bankId;
  const bankName = banksFormat[bankId] || bankId || "-";
  const isIncome =
    tx.type === "RECIEVED" || tx.type === "RECEIVED";
  const typeLabel = isIncome ? "Abono" : "Cargo";
  const amount = tx.amount || 0;
  const currency = tx.currency || "CLP";
  const remaining = tx.remaining ?? amount;
  const isConciliated = remaining === 0;
  const movementDate = tx.date
    ? moment(tx.date).format("DD/MM/YYYY")
    : tx.stringDate || "-";
  const description = tx.description || "-";
  const accountNumber = tx.accountNumber || "-";
  const receiverName = tx.receiverName || "";
  const receiver = tx.receiver || "";
  const issuerName = tx.issuerName || "";
  const issuer = tx.issuer || "";
  const comments = tx.comments || "";

  const counterpartName = isIncome
    ? issuerName || "SIN CONTRAPARTE"
    : receiverName || "SIN CONTRAPARTE";
  const counterpartRut = isIncome ? issuer : receiver;

  const tabs = [
    { key: "detalles", label: "Detalles" },
  ];

  const modalTitle = `${typeLabel} a través del ${bankName} por ${formatMoneyWithCurrency(amount, currency)} con fecha: ${movementDate}`;

  const renderTabContent = () => {
    if (fetching) {
      return <LoadingSpinner />;
    }
    switch (activeTab) {
      case "detalles":
        return (
          <div className="document-content">
            {/* Document Stamp - top right card */}
            <div className="document-stamp">
              <div className="document-stamp-title">
                Movimiento Bancario
              </div>
              <div className="document-stamp-number">
                {typeLabel}
              </div>
              <div className="document-stamp-status">
                <span className={`status-badge ${isConciliated ? "paid" : "pending"}`}>
                  {isConciliated ? "CONCILIADO" : "PENDIENTE"}
                </span>
              </div>
            </div>

            {/* Main Info Grid */}
            <div className="document-grid">
              <div>
                <div className="info-block">
                  <div className="info-label">
                    {isIncome ? "EMITIDO POR" : "EMITIDO A"}
                  </div>
                  <div className="counterpart-name" style={{ fontWeight: 800 }}>
                    {counterpartName}
                  </div>
                  <div className="info-value">
                    <span className="secondary" style={{ fontWeight: 600 }}>
                      {formatRut(counterpartRut) || "-"}
                    </span>
                  </div>
                </div>
                <div className="info-row">
                  <div className="info-block">
                    <div className="info-label">FECHA DEL MOVIMIENTO</div>
                    <div className="info-value">{movementDate}</div>
                  </div>
                  <div className="info-block">
                    <div className="info-label">BANCO</div>
                    <div className="info-value">{bankName}</div>
                  </div>
                  <div className="info-block">
                    <div className="info-label">NÚMERO DE CUENTA</div>
                    <div className="info-value">{accountNumber}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Totals Card */}
            <div className="totals-card">
              <div className="totals-segment">
                <div className="totals-label">MONTO DEL MOVIMIENTO</div>
                <div className="totals-value">
                  {formatMoneyWithCurrency(amount, currency)}
                </div>
              </div>
              {tx.amountCLP && currency !== "CLP" && (
                <div className="totals-segment">
                  <div className="totals-label">EQUIVALENTE EN CLP</div>
                  <div className="totals-value">
                    {formatMoneyWithCurrency(tx.amountCLP, "CLP")}
                  </div>
                </div>
              )}
              <div className="totals-segment">
                <div className="totals-label">MONTO CONCILIADO</div>
                <div className="totals-value">
                  {formatMoneyWithCurrency(amount - remaining, currency)}
                </div>
              </div>
              <div className="totals-segment">
                <div className="totals-label">TOTAL RESTANTE</div>
                <div className="totals-value">
                  {formatMoneyWithCurrency(remaining, currency)}
                </div>
              </div>
            </div>

            {/* Description Section (like Glosa) */}
            <div className="glosa-section">
              <div className="info-label">DESCRIPCIÓN</div>
              <div className="glosa-empty">
                {description !== "-" ? description : "Sin descripción disponible"}
              </div>
            </div>

            {/* Comments Section */}
            {comments && (
              <div className="glosa-section">
                <div className="info-label">COMENTARIOS</div>
                <div className="glosa-empty" style={{ fontStyle: "italic" }}>
                  {comments}
                </div>
              </div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <CustomModal
      open={isVisible}
      onClose={onClose}
      title={modalTitle}
      content={
        <div className="tw-p-4">
          <div className="document-detail-container">
            {/* Tabs */}
            <FilterTabs
              tabs={tabs}
              activeTab={activeTab}
              onTabChange={setActiveTab}
              className="document-filter-tabs"
            />
            {/* Tab Content */}
            <div className="tab-content">
              {renderTabContent()}
            </div>
          </div>
        </div>
      }
      size="large"
      classNameTitle="!tw-text-center !tw-text-[14px] !tw-font-semibold"
      cancelarLabel="Cerrar"
      onCancel={onClose}
      dimmer={{ style: { zIndex: 9999, backgroundColor: "rgba(0,0,0,0.5)" } }}
      modalStyle={{
        width: "800px",
        maxWidth: "80%",
        minWidth: "800px",
        maxHeight: "100vh",
        overflow: "hidden",
        margin: "0 auto",
      }}
      customActions={
        <div className="tw-flex tw-justify-between tw-items-center tw-w-full">
          <Button onClick={onClose}>Cerrar</Button>
        </div>
      }
    />
  );
};

export default ModalTransactionDetail;
